import React from 'react';
import { ForeignActivitiesBenefitTypeItemModel } from 'forms/sections/foreign/activities/benefits/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonRequired, CommonScrollIntoView
} from 'types/props';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import DateControl from 'components/Form/DateControl';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import Show from 'components/Form/Show';
import Textarea from 'components/Form/Textarea';
import Country from 'components/Form/Country';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Branch from 'components/Form/Branch';
import { ErrorCode } from '@redux/modules/application/reducers/error';

interface Props extends Partial<ForeignActivitiesBenefitTypeItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  attribute?: string
}

export default class ContinuingBenefit extends ValidationElement<Props> {
  constructor(props) {
    super(props);
    this.updateCountryFocused = this.updateCountryFocused.bind(this);
  };

  update = (queue: Partial<ForeignActivitiesBenefitTypeItemModel>) => {
    this.props.onUpdate({
      Began: this.props.Began,
      End: this.props.End,
      Frequency: this.props.Frequency,
      OtherFrequency: this.props.OtherFrequency,
      Country: this.props.Country,
      Value: this.props.Value,
      ValueEstimated: this.props.ValueEstimated,
      Reason: this.props.Reason,
      Obligated: this.props.Obligated,
      ObligatedExplanation: this.props.ObligatedExplanation,
      CountryFocused: this.props.CountryFocused,
      ...queue,
    });
  };

  updateBegan = (values: ForeignActivitiesBenefitTypeItemModel['Began']) => {
    this.update({
      Began: values,
    });
  };

  updateEnd = (values: ForeignActivitiesBenefitTypeItemModel['End']) => {
    this.update({
      End: values,
    });
  };

  updateFrequency = (values: ForeignActivitiesBenefitTypeItemModel['Frequency']) => {
    this.update({
      Frequency: values,
    });
  };

  updateOtherFrequency = (values: ForeignActivitiesBenefitTypeItemModel['OtherFrequency']) => {
    this.update({
      OtherFrequency: values,
    });
  };

  updateCountry = (values: ForeignActivitiesBenefitTypeItemModel['Country']) => {
    this.update({
      Country: values,
    });
  };

  updateValue = (values: ForeignActivitiesBenefitTypeItemModel['Value']) => {
    this.update({
      Value: values,
    });
  };

  updateValueEstimated = (values: ForeignActivitiesBenefitTypeItemModel['ValueEstimated']) => {
    this.update({
      ValueEstimated: values,
    });
  };

  updateReason = (values: ForeignActivitiesBenefitTypeItemModel['Reason']) => {
    this.update({
      Reason: values,
    });
  };

  updateObligated = (values: ForeignActivitiesBenefitTypeItemModel['Obligated']) => {
    this.update({
      Obligated: values,
    });
  };

  updateObligatedExplanation = (
    values: ForeignActivitiesBenefitTypeItemModel['ObligatedExplanation']
  ) => {
    this.update({
      ObligatedExplanation: values,
    });
  };

  updateCountryFocused(focus: boolean) {
    this.update({
      CountryFocused: focus,
    });
  };

  render() {
    return (
      <div className="continuing-benefit">
        <Field
          title={i18n.t('foreign.activities.benefit.continuing.heading.began')}
          help="foreign.activities.benefit.continuing.help.began"
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${this.props.attribute}.Began`}
        >
          <DateControl
            name="Began"
            className="began"
            {...this.props.Began}
            onUpdate={this.updateBegan}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${this.props.attribute}.Began`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.benefit.continuing.heading.end')}
          help="foreign.activities.benefit.continuing.help.end"
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${this.props.attribute}.End`}
        >
          <DateControl
            name="End"
            className="end"
            {...this.props.End}
            onUpdate={this.updateEnd}
            minDate={this.props.Began}
            noMaxDate
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${this.props.attribute}.End`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.benefit.continuing.heading.frequency')}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${this.props.attribute}.Frequency`}
        >
          <RadioGroup
            className="frequency option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValue={(this.props.Frequency || {}).value}
            attribute={`${this.props.attribute}.Frequency`}
          >
            <Radio
              name="benefit_frequency"
              label={i18n.t('foreign.activities.benefit.continuing.label.frequency.annually')}
              value="Annually"
              onUpdate={this.updateFrequency}
              onError={this.props.onError}
            />
            <Radio
              name="benefit_frequency"
              label={i18n.t('foreign.activities.benefit.continuing.label.frequency.quarterly')}
              value="Quarterly"
              onUpdate={this.updateFrequency}
              onError={this.props.onError}
            />
            <Radio
              name="benefit_frequency"
              label={i18n.t('foreign.activities.benefit.continuing.label.frequency.monthly')}
              value="Monthly"
              onUpdate={this.updateFrequency}
              onError={this.props.onError}
            />
            <Radio
              name="benefit_frequency"
              label={i18n.t('foreign.activities.benefit.continuing.label.frequency.weekly')}
              value="Weekly"
              onUpdate={this.updateFrequency}
              onError={this.props.onError}
            />
            <Radio
              name="benefit_frequency"
              label={i18n.t('foreign.activities.benefit.continuing.label.frequency.other')}
              value="Other"
              onUpdate={this.updateFrequency}
              onError={this.props.onError}
            />
          </RadioGroup>
          <Show when={(this.props.Frequency || {}).value === 'Other'}>
            <div>
              {i18n.t('foreign.activities.benefit.continuing.para.frequencyOther')}
              <Field attribute={`${this.props.attribute}.OtherFrequency`}>
                <Textarea
                  name="OtherFrequency"
                  className="other-frequency"
                  {...this.props.OtherFrequency}
                  onUpdate={this.updateOtherFrequency}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${this.props.attribute}.OtherFrequency`}
                />
              </Field>
            </div>
          </Show>
        </Field>

        <Field
          title={i18n.t('foreign.activities.benefit.continuing.heading.country')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${this.props.attribute}.Country`}
        >
          <Country
            name="Country"
            {...this.props.Country}
            excludeUnitedStates
            onUpdate={this.updateCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${this.props.attribute}.Country`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.benefit.continuing.heading.value')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${this.props.attribute}.Value`}
        >
          <Currency
            name="Value"
            className="value"
            {...this.props.Value}
            min="0"
            onUpdate={this.updateValue}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${this.props.attribute}.Value`}
          />
          <div className="flags">
            <Checkbox
              name="ValueEstimated"
              label={i18n.t('foreign.activities.benefit.continuing.label.valueEstimated')}
              toggle="false"
              {...this.props.ValueEstimated}
              onUpdate={this.updateValueEstimated}
              onError={this.props.onError}
              attribute={`${this.props.attribute}.ValueEstimated`}
            />
          </div>
        </Field>

        <Field
          title={i18n.t('foreign.activities.benefit.continuing.heading.reason')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${this.props.attribute}.Reason`}
        >
          <Textarea
            name="Reason"
            className="reason"
            {...this.props.Reason}
            onUpdate={this.updateReason}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${this.props.attribute}.Reason`}
          />
        </Field>

        <Branch
          name="Obligated"
          className="obligated no-margin-bottom"
          label={i18n.t('foreign.activities.benefit.continuing.heading.obligated')}
          labelSize="h4"
          {...this.props.Obligated}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateObligated}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${this.props.attribute}.Obligated`}
        />

        <Show when={(this.props.Obligated || {}).value === 'Yes'}>
          <Field
            title={i18n.t('foreign.activities.benefit.continuing.label.obligatedExplanation')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${this.props.attribute}.ObligatedExplanation`}
          >
            <Textarea
              name="Explanation"
              className="explanation"
              {...this.props.ObligatedExplanation}
              onUpdate={this.updateObligatedExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${this.props.attribute}.ObligatedExplanation`}
            />
          </Field>
        </Show>
      </div>
    );
  }
}

ContinuingBenefit.defaultProps = {
  Obligated: {},
  onUpdate: (queue) => { },
  CountryFocused: false,
  onError: (value, arr: ErrorCode) => arr,
};
